import React, { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAccountSubscriptionsQuery } from "fond/api";
import { DesignSubscription, Maybe, MultiProject, Project, Subscription, SubscriptionStatus } from "fond/types";
import { isExtendedPlannerProject } from "fond/utils/project";

export type QuotaUsageDetails = {
  // The total quota available
  AvailableQuota: number;
  // The amount of datapack quota that has ever been bought.
  // Note this is not renewed / reset on a billing cycle.
  DataPackQuota: number;
  // The amount of datapack quota that has ever been used
  // Note this is not renewed / reset on a billing cycle.
  DataPackUsage: number;
  // Base Planner add-on quota available for the billing cycle
  Quota: number;
  // The amount of base Planner add-on quota already used in the billing cycle
  QuotaUsage: number;
};

type ActiveSubscriptionQuotas = (DesignSubscription | DesignSubscription) & Pick<Subscription, "DataPackQuota" | "DataPackUsage">;

/**
 * Hook to return the relevant quota and quota usage for a given project.
 */
export const useProjectQuota = (project: Project | MultiProject | null): Maybe<QuotaUsageDetails> => {
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(project?.Account?.ID ?? skipToken);

  // "FOND design" (planner_extended) projects consume design quota, while other projects consume 'planner' quota.
  const activeSubscription: ActiveSubscriptionQuotas | null = useMemo(() => {
    const subscription = subscriptions?.find(
      (sub) => sub.Status === SubscriptionStatus.Active && (isExtendedPlannerProject(project) ? !!sub.Design : !!sub.Planner)
    );

    const subType = isExtendedPlannerProject(project) ? subscription?.Design : subscription?.Planner;

    return subType
      ? {
          ...subType,
          DataPackQuota: subscription?.DataPackQuota ?? 0,
          DataPackUsage: subscription?.DataPackUsage ?? 0,
        }
      : null;
  }, [project, subscriptions]);

  return useMemo(() => {
    if (!activeSubscription) {
      return null;
    }
    const { Quota, QuotaUsage, DataPackQuota, DataPackUsage } = activeSubscription;
    return {
      Quota,
      QuotaUsage,
      DataPackQuota,
      DataPackUsage,
      AvailableQuota: Quota + DataPackQuota - QuotaUsage - DataPackUsage,
    };
  }, [activeSubscription]);
};
